import React, { useState, useEffect } from 'react';
import { X, Plus, TrendingDown, TrendingUp, Minus, Calendar, Package, Store } from 'lucide-react';
import { getPriceHistory, addPriceEntry, getSuppliers } from '../api';
import { useStructure } from '../contexts/StructureContext';

const PriceHistory = ({ product, selectedPerson, onClose, onPriceAdded }) => {
  const { currentStructure } = useStructure();
  const [priceHistory, setPriceHistory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    supplierId: '',
    price: '',
    quantity: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [product]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [historyResponse, suppliersResponse] = await Promise.all([
        getPriceHistory(product.id),
        getSuppliers(currentStructure.id)
      ]);
      setPriceHistory(historyResponse.data.priceHistory || []);
      setSuppliers(suppliersResponse.data.filter(s => s.active));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Inclure les infos de la personne sélectionnée pour l'historique du stock
      const dataToSend = {
        ...formData,
        ...(selectedPerson && {
          personId: selectedPerson.id,
          personName: `${selectedPerson.firstName} ${selectedPerson.lastName}`,
          personInitials: selectedPerson.initials
        })
      };

      await addPriceEntry(product.id, dataToSend);
      await fetchData();
      setShowAddForm(false);
      setFormData({
        supplierId: '',
        price: '',
        quantity: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      if (onPriceAdded) {
        onPriceAdded();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout du prix');
    }
  };

  const getPriceEvolution = (currentPrice, previousPrice) => {
    if (!previousPrice) return null;
    const current = parseFloat(currentPrice);
    const previous = parseFloat(previousPrice);
    const diff = current - previous;
    if (Math.abs(diff) < 0.01) return { icon: Minus, color: 'text-gray-500', text: '=' };
    if (diff < 0) return { icon: TrendingDown, color: 'text-green-600', text: `${diff.toFixed(2)}€` };
    return { icon: TrendingUp, color: 'text-red-600', text: `+${diff.toFixed(2)}€` };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Historique des prix</h2>
            <p className="text-gray-600">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {/* Add Price Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition"
            >
              <Plus size={20} />
              Enregistrer un nouveau prix
            </button>
          </div>

          {/* Add Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <h3 className="font-semibold mb-4">Enregistrer un achat</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Fournisseur *
                    </label>
                    <select
                      value={formData.supplierId}
                      onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Prix unitaire * (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Quantité
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Date d'achat
                    </label>
                    <input
                      type="date"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    rows="2"
                    placeholder="Ex: Promo -20%, achat en gros..."
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                  >
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Price History List */}
          {loading ? (
            <div className="text-center py-8">Chargement...</div>
          ) : priceHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-400" />
              <p>Aucun historique de prix</p>
              <p className="text-sm">Enregistrez votre premier achat pour commencer</p>
            </div>
          ) : (
            <div className="space-y-3">
              {priceHistory.map((entry, index) => {
                const evolution = index < priceHistory.length - 1
                  ? getPriceEvolution(entry.price, priceHistory[index + 1].price)
                  : null;
                const EvolutionIcon = evolution?.icon;

                return (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-bold text-primary">
                            {parseFloat(entry.price).toFixed(2)}€
                          </span>
                          {evolution && (
                            <span className={`flex items-center gap-1 text-sm ${evolution.color}`}>
                              <EvolutionIcon size={16} />
                              {evolution.text}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-gray-700 mb-1">
                          <Store size={16} />
                          <span className="font-medium">{entry.supplier?.name || 'N/A'}</span>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {new Date(entry.recordedAt).toLocaleDateString('fr-FR')}
                          </span>
                          {entry.quantity && (
                            <span className="flex items-center gap-1">
                              <Package size={14} />
                              Quantité: {entry.quantity}
                            </span>
                          )}
                        </div>

                        {entry.notes && (
                          <div className="mt-2 text-sm text-gray-600 italic">
                            {entry.notes}
                          </div>
                        )}

                        <div className="mt-2 text-xs text-gray-500">
                          Enregistré le{' '}
                          {new Date(entry.recordedAt).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceHistory;
